import React, { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { approvalPopupState } from "./ApprovalPopupState";
import ProfileSummary from "Pages/Dashboard/DeepResearch/components/ProfileSummary";
import LeadershipProfile from "Pages/Dashboard/DeepResearch/components/LeadershipProfile";
import SellingSignals from "Pages/Dashboard/DeepResearch/components/SellingSignals";
import ConversationStarters from "Pages/Dashboard/DeepResearch/components/ConversationStarters";
import KeyMentions from "Pages/Dashboard/DeepResearch/components/KeyMentions";
import CompanyOverview from "Pages/Dashboard/DeepResearch/components/CompanyOverview";
import QuilEditor from "Components/common/quillEditor";
import Utils from "Utils/utils";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import {
  LetterAvatar,
  LinkedInBadge
} from "Pages/Dashboard/DeepResearch/components/ContactIdentity";

const STATE_LABELS = {
  AWAITING_APPROVAL: "Awaiting approval",
  SCHEDULED: "Scheduled",
  SENT: "Sent",
  SENDING_FAILED: "Send failed",
  CANCELLED: "Cancelled"
};

// Awaiting is implicit when this popup is open — only badge non-default states.
const NOTABLE_STATES = new Set(["SENT", "SCHEDULED", "SENDING_FAILED", "CANCELLED"]);

const formatScheduled = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date} · ${time}`;
};

// First-letter avatar + LinkedIn badge come from ContactIdentity. Helpers there.

// Map every taskType variant to a channel family so cards can carry a
// channel-colored left border without scattering switch-cases.
const CHANNEL_FOR_TYPE = {
  email: "email", aiEmail: "email", manualEmail: "email", aiManualEmail: "email",
  linkedin: "linkedin", linkedinAuto: "linkedin",
  aiLinkedin: "linkedin", aiLinkedinAuto: "linkedin",
  phone: "phone", aiPhone: "phone",
  whatsappAuto: "whatsapp", aiWhatsappAuto: "whatsapp",
  sms: "sms"
};
const channelFor = (t) => CHANNEL_FOR_TYPE[t] || "default";

const STEP_TYPE_LABELS = {
  // Ai-prefixed types (legacy)
  aiEmail: "Email",
  aiManualEmail: "Email (manual)",
  aiLinkedinAuto: "LinkedIn",
  aiLinkedin: "LinkedIn (manual)",
  aiPhone: "Call",
  aiWhatsappAuto: "WhatsApp",
  // Plain types as actually persisted in drs_task.taskType
  email: "Email",
  manualEmail: "Email (manual)",
  linkedinAuto: "LinkedIn",
  linkedin: "LinkedIn (manual)",
  phone: "Call",
  whatsappAuto: "WhatsApp"
};

const isEmailType = t =>
  ["aiEmail", "aiManualEmail", "email", "manualEmail"].includes(t);

const looksLikeHtml = s =>
  typeof s === "string" && /<\/?(p|div|br|span|strong|em|a|ul|ol|li|h[1-6])\b/i.test(s);

// Plain-text drs_task content → HTML the Quill editor can edit cleanly.
// Treats blank lines as paragraph breaks; preserves single \n as <br>.
const seedEditorHtml = raw => {
  if (!raw) return "";
  if (looksLikeHtml(raw)) return raw;
  return raw
    .split(/\n{2,}/)
    .map(p => `<p>${p.replace(/\n/g, "<br>")}</p>`)
    .join("");
};

const ResearchSummaryView = ({ data, contactLinkedinUrl }) => {
  if (!data || typeof data !== "object") {
    return <div className="drApprovalResearchEmpty">No research available.</div>;
  }
  const userInsights = data.user_insights || {};
  const sellingSignals =
    userInsights.selling_opportunities || data.sellingSignals || [];
  const conversationStarters =
    userInsights.conversation_starters || data.conversationStarters || [];
  const keyMentions =
    userInsights.key_mentions || data.keyMentions || [];
  const structuredProfile =
    userInsights.structured_profile || data.ai_insights || data.structuredProfile || {};
  const dataQuality = data.data_quality || data.dataQuality || {};
  const aiInsightsForLeadership =
    data.profileData?.ai_insights || data.ai_insights || data.aiInsights || {};
  const languages = data.profileData?.languages || data.languages;
  const companyData = data.company_data || data.companyData || {};
  const companyInsights = data.company_insights || data.companyInsights || {};
  const deepDiveIntel = data.deep_dive_intel || data.deepDiveIntel || {};

  const hasAny =
    Object.keys(structuredProfile).length ||
    Object.keys(aiInsightsForLeadership).length ||
    sellingSignals.length ||
    conversationStarters.length ||
    keyMentions.length ||
    Object.keys(companyData).length ||
    Object.keys(companyInsights).length;

  if (!hasAny) {
    return (
      <div className="drApprovalResearchEmpty">
        Research is still being prepared.
      </div>
    );
  }

  return (
    <div className="drApprovalResearchView">
      <ProfileSummary
        profileData={data}
        structuredProfile={structuredProfile}
        dataQuality={dataQuality}
        contactLinkedinUrl={contactLinkedinUrl}
      />
      <LeadershipProfile
        aiInsights={aiInsightsForLeadership}
        languages={languages}
      />
      {sellingSignals.length > 0 && (
        <SellingSignals signals={sellingSignals} />
      )}
      {conversationStarters.length > 0 && (
        <ConversationStarters starters={conversationStarters} />
      )}
      {keyMentions.length > 0 && <KeyMentions mentions={keyMentions} />}
      <CompanyOverview
        companyData={companyData}
        companyInsights={companyInsights}
        deepDiveIntel={deepDiveIntel}
      />
    </div>
  );
};

// Read-only row + action buttons. Edit opens a full-screen page (see
// MessageEditPage). Regenerate lives at the contact level, not per message,
// since regenerating one touch in isolation breaks narrative coherence with
// the other touches in the arc.
const MessageRow = observer(({ msg, stepNumber, onEdit }) => {
  const isSent = msg.messageState === "SENT";
  const isCancelled = msg.messageState === "CANCELLED";
  const isReadOnly = isSent || isCancelled;

  const stepLabel = STEP_TYPE_LABELS[msg.taskType] || msg.taskType;
  const stateLabel = STATE_LABELS[msg.messageState] || msg.messageState;
  const scheduledLabel = formatScheduled(msg.scheduledFor);
  const showStateBadge = NOTABLE_STATES.has(msg.messageState);

  return (
    <div className={`drApprovalMsg drApprovalMsg--${channelFor(msg.taskType)} ${isReadOnly ? "drApprovalMsgReadOnly" : ""}`}>
      <div className="drApprovalMsgHeader">
        <span className="drApprovalMsgStep">
          Step {stepNumber} · {stepLabel}
        </span>
        {scheduledLabel && (
          <span className="drApprovalMsgSchedule" title="Will send at this time once approved">
            {scheduledLabel}
          </span>
        )}
        {showStateBadge && (
          <span className={`drApprovalMsgState drApprovalMsgState--${msg.messageState}`}>
            {stateLabel}
          </span>
        )}
        {msg.editedByUser && <span className="drApprovalMsgEdited">EDITED</span>}
      </div>

      <div className="drApprovalMsgContent">
        {msg.subject && <div className="drApprovalMsgSubject">{msg.subject}</div>}
        {isEmailType(msg.taskType) && looksLikeHtml(msg.content) ? (
          <div
            className="drApprovalMsgBody drApprovalMsgBodyHtml"
            dangerouslySetInnerHTML={{ __html: msg.content }}
          />
        ) : (
          <div className="drApprovalMsgBody">{msg.content || "(no content)"}</div>
        )}
      </div>

      {!isReadOnly && (
        <div className="drApprovalMsgActions">
          <button onClick={() => onEdit(msg.id)}>Edit</button>
          {msg.messageState === "SCHEDULED" && (
            <button onClick={() => approvalPopupState.cancelScheduled(msg.id)} className="danger">Cancel send</button>
          )}
        </div>
      )}
    </div>
  );
});

// Full-screen edit view inside the popup. Replaces the message list while active.
const MessageEditPage = observer(({ msg, contactName, onBack }) => {
  const [draftSubject, setDraftSubject] = useState(msg.subject || "");
  const [draftContent, setDraftContent] = useState(msg.content || "");
  const [saving, setSaving] = useState(false);

  const stepLabel = STEP_TYPE_LABELS[msg.taskType] || msg.taskType;
  const isEmail = isEmailType(msg.taskType);

  // Seed Quill ONCE per message — recomputing prefilledValue on every keystroke
  // makes the editor re-init and snaps the cursor back to the start.
  const initialEditorHtml = useMemo(
    () => seedEditorHtml(msg.content || ""),
    [msg.id] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSave = () => {
    setSaving(true);
    Promise.resolve(
      approvalPopupState.editMessage(msg.id, {
        subject: draftSubject,
        content: draftContent
      })
    ).finally(() => {
      setSaving(false);
      onBack();
    });
  };

  return (
    <div className="drApprovalEditPage">
      <div className="drApprovalEditHeader">
        <button className="drApprovalEditBack" onClick={onBack}>
          ← Back
        </button>
        <div className="drApprovalEditTitle">
          Editing {stepLabel.toLowerCase()}
          {contactName ? ` · ${contactName}` : ""}
        </div>
      </div>
      <div className="drApprovalEditBody">
        {isEmail && (
          <input
            type="text"
            value={draftSubject}
            onChange={e => setDraftSubject(e.target.value)}
            placeholder="Subject"
            className="drApprovalSubjectInput"
          />
        )}
        {isEmail ? (
          <div className="drApprovalRichEditor">
            <QuilEditor
              key={`drs-edit-${msg.id}`}
              prefilledValue={initialEditorHtml}
              bodyOnChange={value => setDraftContent(value)}
              stageType={msg.taskType}
              type="sequences"
              pageType="sequences"
              showRewriteAI={false}
              showVariations={false}
            />
          </div>
        ) : (
          <textarea
            value={draftContent}
            onChange={e => setDraftContent(e.target.value)}
            className="drApprovalPlainEditor"
          />
        )}
      </div>
      <div className="drApprovalEditFooter">
        <button onClick={onBack} disabled={saving}>Cancel</button>
        <button className="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
});

// ─── Loading states ────────────────────────────────────────────────
// Shared visual language across regen + initial-open + contact-nav so the
// popup never falls back to dead "Loading…" text. Progress bar + rotating
// status copy makes the wait feel purposeful.

const REGEN_STATUS_MESSAGES = [
  "Pulling research signals…",
  "Re-threading the arc…",
  "Writing each touch…",
  "Polishing the drafts…"
];

const INITIAL_LOAD_MESSAGES = [
  "Finding contacts awaiting your approval…",
  "Loading personalized messages…",
  "Almost ready…"
];

const CONTACT_NAV_MESSAGES = [
  "Loading messages for this contact…",
  "Pulling research insights…",
  "Almost there…"
];

// Right-panel research load. Rotating copy that mirrors what the BE is
// actually doing (LinkedIn pull → activity scan → mapping signals → hooks)
// so the wait reads as purposeful, not opaque.
const RESEARCH_LOAD_MESSAGES = [
  "Pulling LinkedIn signals…",
  "Scanning recent posts and activity…",
  "Mapping triggers to your offer…",
  "Surfacing conversation hooks…",
  "Stitching the brief together…"
];

// Generic body for the regen overlay AND the inline (open / nav) loading
// panels. Caller controls layout via wrapper className.
const LoadingPanelBody = ({ title, messages, hint }) => {
  const [statusIdx, setStatusIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setStatusIdx(i => (i + 1) % messages.length),
      2500
    );
    return () => clearInterval(t);
  }, [messages.length]);
  return (
    <>
      <div className="drApprovalRegenBar">
        <div className="drApprovalRegenBarFill" />
      </div>
      <div className="drApprovalRegenBody">
        {title && <div className="drApprovalRegenTitle">{title}</div>}
        <div key={statusIdx} className="drApprovalRegenStatus">
          {messages[statusIdx]}
        </div>
        {hint && <div className="drApprovalRegenHint">{hint}</div>}
      </div>
    </>
  );
};

// Whole-arc regen — full-card overlay with backdrop blur. Blocks interaction.
const RegenLoadingOverlay = () => (
  <div className="drApprovalRegenOverlay">
    <LoadingPanelBody
      title="Regenerating the whole arc"
      messages={REGEN_STATUS_MESSAGES}
      hint="This usually takes 10–15 seconds. Please don't close the window."
    />
  </div>
);

// Inline panel — fills its container (used when there's no content to overlay yet).
const ApprovalLoadingPanel = ({ title, messages, hint }) => (
  <div className="drApprovalLoadingPanel">
    <LoadingPanelBody title={title} messages={messages} hint={hint} />
  </div>
);

const ApprovalPopup = observer(() => {
  const {
    showPopup,
    contacts,
    currentIndex,
    messages,
    loadingContacts,
    loadingMessages,
    saving,
    regeneratingAll,
    showResearchSummary,
    researchSummary
  } = approvalPopupState;

  // Edit-page navigation: when set, the popup body renders the full-screen
  // editor for this message instead of the contact's message list.
  const [editingMessageId, setEditingMessageId] = useState(null);

  // Reset edit-page state when the popup closes or the active contact changes.
  useEffect(() => {
    if (!showPopup) setEditingMessageId(null);
  }, [showPopup]);
  useEffect(() => {
    setEditingMessageId(null);
  }, [currentIndex]);

  // Keyboard shortcuts: Esc closes (or backs out of edit mode), ← / → walk between contacts.
  // Disabled entirely while a whole-arc regen is in flight — rep should wait it out.
  useEffect(() => {
    if (!showPopup) return;
    const onKey = (e) => {
      if (regeneratingAll) return;
      if (e.key === "Escape") {
        if (editingMessageId) setEditingMessageId(null);
        else approvalPopupState.close();
      }
      if (e.key === "ArrowLeft" && (e.metaKey || e.altKey) && !editingMessageId) approvalPopupState.goPrev();
      if (e.key === "ArrowRight" && (e.metaKey || e.altKey) && !editingMessageId) approvalPopupState.goNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showPopup, editingMessageId, regeneratingAll]);

  // Self-gate: even if some caller opens the popup state, render nothing
  // when the team doesn't have sequence DR enabled. Defense in depth — the
  // BE also rejects approval endpoints for non-enabled teams.
  if (!Utils.isSequenceDRFeatureEnabled()) return null;
  if (!showPopup) return null;

  if (loadingContacts) {
    return (
      <div className="drApprovalOverlay">
        <div className="drApprovalCard">
          <ApprovalLoadingPanel
            title="Loading contacts"
            messages={INITIAL_LOAD_MESSAGES}
          />
        </div>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="drApprovalOverlay" onClick={() => approvalPopupState.close()}>
        <div className="drApprovalCard" onClick={e => e.stopPropagation()}>
          <div className="drApprovalEmpty">
            <h3>No contacts awaiting approval</h3>
            <p>You're all caught up.</p>
            <button onClick={() => approvalPopupState.close()} className="primary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const contact = contacts[currentIndex];
  const awaitingCount = messages.filter(m => m.messageState === "AWAITING_APPROVAL").length;
  const editingMsg = editingMessageId
    ? messages.find(m => m.id === editingMessageId)
    : null;

  return (
    <div
      className="drApprovalOverlay"
      onClick={() => {
        if (regeneratingAll) return; // hold the rep on the screen during regen
        approvalPopupState.close();
      }}
    >
      <div
        className={`drApprovalCard ${showResearchSummary && !editingMsg ? "withResearch" : ""} ${editingMsg ? "isEditing" : ""} ${regeneratingAll ? "isRegenerating" : ""}`}
        onClick={e => e.stopPropagation()}
      >
        {regeneratingAll && <RegenLoadingOverlay />}
        {editingMsg ? (
          <MessageEditPage
            msg={editingMsg}
            contactName={contact.name || contact.email || contact.contactId}
            onBack={() => setEditingMessageId(null)}
          />
        ) : (
          <>
            {/* Header */}
            <div className="drApprovalHeader">
              <div className="drApprovalNav">
                <button onClick={() => approvalPopupState.goPrev()} disabled={currentIndex === 0}>◀ Prev</button>
                <div className="drApprovalProgress">
                  {contacts.length <= 10 ? (
                    <span className="drApprovalProgressDots">
                      {contacts.map((_, i) => (
                        <span
                          key={i}
                          className={`drApprovalDot ${i === currentIndex ? "current" : ""} ${i < currentIndex ? "past" : ""}`}
                        />
                      ))}
                    </span>
                  ) : (
                    <span className="drApprovalProgressBar">
                      <span
                        className={`drApprovalProgressBarFill fill-${Math.min(100, Math.round(((currentIndex + 1) / contacts.length) * 10) * 10)}`}
                      />
                    </span>
                  )}
                  <span className="drApprovalCounter">
                    Contact {currentIndex + 1} of {contacts.length}
                  </span>
                </div>
                <button onClick={() => approvalPopupState.goNext()} disabled={currentIndex >= contacts.length - 1}>Next ▶</button>
              </div>
              <span className="drApprovalClose" onClick={() => approvalPopupState.close()}>✕</span>
            </div>

            <div className="drApprovalDescription">
              Each message is mapped to a step in this sequence — once approved, they'll send on their scheduled day. Edit, regenerate, or reject the contact.
            </div>

            <div className="drApprovalSubheader">
              <LetterAvatar
                seed={contact.name || contact.email || contact.contactId}
                size="md"
              />
              <div className="drApprovalContactInfo">
                <div className="drApprovalContactName">
                  <span className="drApprovalContactNameText">
                    {contact.name || contact.email || contact.contactId}
                  </span>
                  <LinkedInBadge url={contact.linkedinUrl} />
                </div>
                {(contact.jobTitle || contact.company) && (
                  <div className="drApprovalContactMeta">
                    {[contact.jobTitle, contact.company].filter(Boolean).join(" · ")}
                  </div>
                )}
                {contact.location && (
                  <div className="drApprovalContactLocation" title={contact.location}>
                    <span className="drApprovalContactLocationPin">📍</span>
                    {contact.location}
                  </div>
                )}
                {contact.email && contact.name && (
                  <div className="drApprovalContactEmail" title={contact.email}>
                    {contact.email}
                  </div>
                )}
              </div>
              <div className="drApprovalSubheaderActions">
                <button
                  className="drApprovalRegenAllBtn"
                  onClick={() => {
                    confirmationPopupState.setPopupValues({
                      content: `<span class="drApprovalConfirmTitle">Are you sure you want to regenerate all messages for this contact?</span><span class="drApprovalConfirmHint">Every touch will be re-threaded together and your edits will be replaced.</span>`,
                      actionBtnText: "Yes",
                      cancelBtnText: "Cancel",
                      cancelCbk: () =>
                        confirmationPopupState.setShowPopup(false),
                      callback: () =>
                        approvalPopupState.regenerateAllForCurrentContact()
                    });
                    confirmationPopupState.setShowPopup(true);
                  }}
                  disabled={saving || loadingMessages || messages.length === 0}
                  title="Regenerate every message in one shot so the whole arc threads together"
                >
                  {regeneratingAll ? "Regenerating…" : "Regenerate all"}
                </button>
                <button className="drApprovalSummaryToggle" onClick={() => approvalPopupState.toggleResearchSummary()}>
                  {showResearchSummary ? "Hide research" : "Show research"}
                </button>
              </div>
            </div>

            {/* Body — main column + optional right research panel */}
            <div className="drApprovalBody">
              <div className="drApprovalMessages">
                {loadingMessages ? (
                  <ApprovalLoadingPanel
                    title="Loading messages"
                    messages={CONTACT_NAV_MESSAGES}
                  />
                ) : messages.length === 0 ? (
                  <div className="drApprovalEmpty">No messages for this contact.</div>
                ) : (
                  messages.map((m, idx) => (
                    <MessageRow
                      key={m.id}
                      msg={m}
                      stepNumber={idx + 1}
                      onEdit={setEditingMessageId}
                    />
                  ))
                )}
              </div>

              {showResearchSummary && (
                <div className="drApprovalResearchPanel">
                  <div className="drApprovalResearchHeader">
                    <h4>Research</h4>
                    <a
                      className="drApprovalResearchOpenFull"
                      href={`/contacts/${contact.contactId}/deepresearch`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open full →
                    </a>
                  </div>
                  {researchSummary ? (
                    <ResearchSummaryView
                      data={researchSummary}
                      contactLinkedinUrl={contact.linkedinUrl}
                    />
                  ) : (
                    <ApprovalLoadingPanel
                      title="Loading research"
                      messages={RESEARCH_LOAD_MESSAGES}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="drApprovalFooter">
              <button
                onClick={() => {
                  confirmationPopupState.setPopupValues({
                    content: `<span class="drApprovalConfirmTitle">Are you sure you want to reject this contact?</span><span class="drApprovalConfirmHint">They will be stopped in this sequence and won't receive any of these messages.</span>`,
                    actionBtnText: "Yes",
                    cancelBtnText: "Cancel",
                    cancelCbk: () =>
                      confirmationPopupState.setShowPopup(false),
                    callback: () => approvalPopupState.rejectCurrentContact()
                  });
                  confirmationPopupState.setShowPopup(true);
                }}
                disabled={saving}
                className="danger"
              >
                Reject contact
              </button>
              <button
                onClick={() => approvalPopupState.approveCurrentContact()}
                disabled={saving || awaitingCount === 0}
                className={`primary ${saving && !regeneratingAll ? "isProcessing" : ""}`}
              >
                {saving && !regeneratingAll ? (
                  <span className="drApprovalBtnSpinner">
                    <span className="drApprovalSpinDot" />
                    <span className="drApprovalSpinDot" />
                    <span className="drApprovalSpinDot" />
                    <span>Approving</span>
                  </span>
                ) : awaitingCount > 0 ? (
                  `Approve all ${awaitingCount} message${awaitingCount > 1 ? "s" : ""}`
                ) : (
                  "Nothing to approve"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default ApprovalPopup;
